/**
 * W4-3 / 4e — arrival notices to third parties (staff contacts).
 *
 * A contact is a NON-guest person the organiser names per event (the
 * caterer, the host at the door) — a REGISTRY entry: address, label,
 * language. Only an automation's "Mail an Kontakt" (`NOTIFY_CONTACT`) mails
 * it, a SYSTEM notice carrying only the allow-listed native fields (plan
 * legacy-out Rev. 3: the old per-contact triggers / audience form is gone).
 * The address must be verified (confirm link, 24 h) before the first notice;
 * an unverified contact is removed automatically after 7 days; every notice
 * carries a one-click removal link. Mirrors main-backend
 * `org.nikita.app.event_notification` (store `event_notification_contacts`).
 */
export const ARRIVAL_TRIGGERS = [
  "GUEST_CHECKIN",
  "GUEST_CHECKIN_APP",
  "GUEST_SEATING_CHECKIN_APP",
  "GUEST_APPOINTMENT_CHECKIN_APP",
] as const;
export type ArrivalTrigger = (typeof ARRIVAL_TRIGGERS)[number];

/**
 * Fixed NATIVE fields only (plan review r1 H5): a custom field's
 * sensitivity is organiser-set and an unclassified "Allergien" falls back to
 * NORMAL, so no `additionalFields.*` key is offered here — the backend
 * refuses any with 400 `fieldNotAllowedForNotification`.
 */
export const ARRIVAL_FIELDS = [
  "firstName",
  "lastName",
  "company",
  "jobTitle",
  "guestType",
  "numCompanions",
  "seat",
  "table",
] as const;
export type ArrivalField = (typeof ARRIVAL_FIELDS)[number];

/**
 * The caps (contract-b §2). The GET returns them as `caps`; these are only
 * the fallback for a reply without them.
 */
export interface NotificationContactCaps {
  /** Contacts per event (`notificationContactLinkedCapReached`). */
  linked: number;
  /** Guests per contact (`notificationContactGuestLinksCapReached`). */
  guestsPerContact: number;
  /** Contacts per guest (`notificationContactsPerGuestCapReached`). */
  contactsPerGuest: number;
}
export const DEFAULT_NOTIFICATION_CONTACT_CAPS: NotificationContactCaps = {
  linked: 200,
  guestsPerContact: 2000,
  contactsPerGuest: 5,
};
/** Addresses per `POST /ensure` call (the import's bulk find-or-create). */
export const ENSURE_MAX_EMAILS = 200;

/** Where the organiser's privacy URL was found (website first, then portal). */
export type PrivacyUrlSource = "WEBSITE" | "PORTAL";
/** Confirm mails per address per day (backend `verificationMailCapReached` 429). */
export const VERIFICATION_MAILS_PER_DAY = 3;
/** Days after which an unverified contact is removed automatically. */
export const UNVERIFIED_REMOVAL_DAYS = 7;

/**
 * A contact as the API answers it. `triggers`, `fields`, `cockpitBulk` and
 * `audience` are still in every answer (main-backend `toApi` is unchanged —
 * the stored registry shape: `[]`, `"LINKED_GUESTS"`, …) but the cockpit
 * neither reads nor writes them: no write body carries them (409
 * `notificationContactPageOutdated`).
 */
export interface NotificationContact {
  eventNotificationContactId: string;
  email: string;
  label: string;
  triggers?: ArrivalTrigger[];
  fields?: ArrivalField[];
  cockpitBulk?: boolean;
  language: string;
  verifiedAt: string | null;
  removedAt: string | null;
  createdAt: string;
  verifyMailsToday: number;
  audience?: string | null;
  /** Linked guests (`$size` server-side); the ids themselves never leave the server. */
  guestCount?: number;
}

/**
 * The contact's label, if it has one (trimmed; "" when blank). The label is
 * optional on every write — an import creates its contacts with `label: ""`.
 */
export function contactLabelOf(contact: Pick<NotificationContact, "label">): string {
  return (contact.label ?? "").trim();
}

/**
 * The name a contact is shown and announced by (row title, dialog, aria-label):
 * its label, or its address when the label is blank (audit r1 A5-5).
 */
export function contactDisplayLabel(contact: Pick<NotificationContact, "label" | "email">): string {
  return contactLabelOf(contact) || contact.email;
}

/**
 * `guestFieldPipe` (§1.1): whether any pipe of the event has a NOTIFY_CONTACT
 * in guest-field mode — none, only paused ones (`off`), or an enabled one
 * (`on`). Drives the guest field's and the registry page's hints.
 */
export type GuestFieldPipeState = "none" | "off" | "on";

/**
 * The keys the list and forGuest GETs append (§1.1); optional on the wire.
 * The GETs also still carry `mode` (constant `"automation"` since plan
 * legacy-out Rev. 3) — the cockpit never reads it: every event is served by
 * automations only.
 */
export interface ArrivalRegistryView {
  actionsPlugin?: boolean | null;
  guestFieldPipe?: GuestFieldPipeState | string | null;
}

/** `guestFieldPipe` read fail-safe: anything but `on` / `off` is `none`. */
export function guestFieldPipeOf(view: ArrivalRegistryView | null | undefined): GuestFieldPipeState {
  return view?.guestFieldPipe === "on" || view?.guestFieldPipe === "off" ? view.guestFieldPipe : "none";
}

/**
 * The guest-field / registry warnings (§1.8): the plugin "Aktionen" is off
 * (nothing runs), or no ENABLED pipe mails the contacts the guest field
 * names. `null` = nothing to warn about. No read at all (`null` /
 * `undefined`) warns of nothing — the caller shows its own "unavailable".
 */
export type ArrivalAutomationWarning = "actionsOff" | "noGuestFieldPipe";

export function arrivalAutomationWarning(
  view: ArrivalRegistryView | null | undefined,
): ArrivalAutomationWarning | null {
  if (!view) return null;
  if (view.actionsPlugin === false) return "actionsOff";
  if (guestFieldPipeOf(view) !== "on") return "noGuestFieldPipe";
  return null;
}

export interface NotificationContactList extends ArrivalRegistryView {
  contacts: NotificationContact[];
  caps?: Partial<NotificationContactCaps>;
  /**
   * The organiser's privacy URL the mails link (decision 11 b): website
   * config first, then the portal; `null` = none usable, and then no contact
   * can be added (409 `organiserPrivacyUrlMissing`). Absent = a backend from
   * before the gate.
   */
  privacyUrl?: string | null;
  privacyUrlSource?: PrivacyUrlSource | null;
}

/**
 * The settings page's ONLY write body (plan arrival-automation Rev. 4 §1.4,
 * legacy-out Rev. 3): label, address and language. `triggers`, `fields`,
 * `cockpitBulk` and `audience` are never sent — main-backend answers ANY
 * non-null value of them (even `[]`, `false` or `"LINKED_GUESTS"`) with 409
 * `notificationContactPageOutdated`; the client sends exactly these keys.
 */
export interface NotificationContactRegistryUpsert {
  eventNotificationContactId?: string;
  email: string;
  label: string;
  language: string;
}

export interface ResendVerificationResult {
  sentToday: number;
}

/** `GET …/guests/{guestId}` — what the guest popup shows. */
export interface GuestNotificationContacts extends ArrivalRegistryView {
  /** The contacts this guest is linked to (unlinkable here). */
  own: NotificationContact[];
  /** A companion's main guest's contacts (strict inherit rule; read-only). */
  inherited: NotificationContact[];
}

/** `PUT …/guests/{guestId}` body: find-or-create a contact for this address, then link. */
export interface GuestNotificationContactLink {
  email: string;
  label: string;
  language: string;
}

export type EnsureNotificationContactStatus = "CREATED" | "EXISTING" | "REFUSED";

/** `POST …/ensure` body — the import's bulk find-or-create (≤ ENSURE_MAX_EMAILS addresses). */
export interface EnsureNotificationContactsRequest {
  emails: string[];
  language: string;
  /** true = count only, nothing written, no mail. */
  dryRun: boolean;
}

export interface EnsureNotificationContactResult {
  email: string;
  status: EnsureNotificationContactStatus;
  /** Hex id; absent on REFUSED (and on a dry-run CREATED). */
  contactId?: string;
  /** The refusal code (e.g. `notificationContactAddressSuppressed`) on REFUSED. */
  code?: string;
}

export interface EnsureNotificationContactsResponse {
  results: EnsureNotificationContactResult[];
  wouldSendConfirmMails: number;
}

/**
 * plan arrival-automation Rev. 4 §1.4 — `GET …/notification-contacts/picker`,
 * the NOTIFY_CONTACT editor's contact choice. An in-person OWNER / ADMIN gets
 * the full item (`label`, `maskedEmail`, `verified`, `guestCount` — never the
 * address itself); every other role that may read automations gets the
 * reduced one (`label: "Kontakt N"`, `verified`) and `reduced: true`. A
 * backend without the route (a pre-arm rollback) answers an error: the
 * editor then does not offer the action at all (R2-L9).
 */
export interface NotificationContactPickerItem {
  id: string;
  label: string;
  verified: boolean;
  /** Full shape only, e.g. `g***@c***.de`. */
  maskedEmail?: string;
  /** Full shape only — linked guests. */
  guestCount?: number;
}

export interface NotificationContactPicker {
  contacts: NotificationContactPickerItem[];
  reduced: boolean;
}
